import React, { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "styled-components";

import "./App.css";
import AccountPage from "./UI/AccountPage";
import AccountCreationPage from "./UI/AccountCreationPage";
import CredentialLoginPage from "./UI/CredentialLoginPage";
import ErrorPage from "./UI/ErrorPage";
import LandingPage from "./UI/LandingPage";
import LoginPage from "./UI/LoginPage";

import logoIndicio from "./assets/png/indicio-logo.png";
import indicioBankingLogo from "./assets/svg/indicio-banking-logo.svg";

import {
  Frame,
  Header,
  HeaderWrapperLeft,
  HeaderWrapperRight,
  HeaderOption,
  MainWrapper,
  Footer,
  FooterWrapper,
  FooterWrapperFirst,
  FooterWrapperSecond,
  FooterWrapperThird,
  ButtonDark,
  ButtonLight,
  SearchIcon,
  DropdownArrowIcon,
  Logo,
} from "./StyledComponents/AppStyles";

const theme = {
  firstColor: "#12375A",
  secondColor: "#2A5D8D",
  thirdColor: "#F1F4FF",
  fourthColor: "#E3B455",
  fifthColor: "#FFEABF",
  sixthColor: "#0A0F0D",
  seventhColor: "#324F6A",
  eighthColor: "#7C7C7C",
  ninthColor: "#B1B1B1",
  tenthColor: "#FFFFFF",
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [connection, setConnection] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [error, setError] = useState(false);
  const [invitation, setInvitation] = useState({});
  const [userData, setUserData] = useState({
    givenName: "",
    familyName: "",
    userId: "",
    dateOfBirth: "",
    phoneNumber: "",
    email: "",
    accountId: "",
    accountEstablishedDate: "",
  });
  const [verifiedCredAttributes, setVerifiedCredAttributes] = useState({});

  const createInvitation = () => {
    Axios({
      method: "POST",
      url: "/api/invitations",
    })
      .then((res) => {
        console.log("Invitation response: ", res.data);
        setInvitation(res.data);
      })
      .catch((error) => {
        console.log("Invitation Error: ", error);
        setError(true);
      });
  };

  const checkConnection = () => {
    Axios({
      method: "GET",
      url: `/api/connections/${invitation.invitation_id}`,
    })
      .then((res) => {
        console.log("Connections response: ", res.data);
        setConnection(true);
      })
      .catch((error) => {
        console.log("Connection Error: ", error);
        setError(true);
      });
  };

  const fetchVerification = (schema) => {
    Axios({
      method: "POST",
      url: "/api/verifications",
      data: {
        invitation_id: invitation.invitation_id,
        schema,
      },
    })
      .then((res) => {
        console.log("Verifications response: ", res.data);

        const verifiedAttributes = {};
        res.data.verificationRecords[0].result_data.forEach((attribute) => {
          verifiedAttributes[attribute.name] = attribute.value;
        });

        setVerifiedCredAttributes(verifiedAttributes);
      })
      .catch((error) => {
        console.log("Verification Error: ", error);
        setError(true);
      });
  };

  const issueCredentials = (schemaName, credentialData) => {
    Axios({
      method: "POST",
      url: "/api/credentials",
      data: {
        contact_id: invitation.contact_id,
        invitation_id: invitation.invitation_id,
        schemaName,
        credentialData,
      },
    })
      .then((res) => {
        console.log("Credential response: ", res.data);
        setCredentials(res.data.issuanceRecord);
      })
      .catch((error) => {
        console.log("Credential Issuance Error: ", error);
        setError(true);
      });
  };

  useEffect(() => {
    if (error) {
      navigate("/error");
    }
  }, [error]);

  useEffect(() => {
    if (Object.keys(invitation).length !== 0) {
      checkConnection();
    }
  }, [invitation]);

  //(AmmonBurgi) Reset app state when navigating to certain pages
  const resetAppState = useCallback(() => {
    setConnection(false);
    setCredentials({});
    setInvitation({});
    setUserData({
      givenName: "",
      familyName: "",
      userId: "",
      dateOfBirth: "",
      phoneNumber: "",
      email: "",
      accountId: "",
      accountEstablishedDate: "",
    });
    setVerifiedCredAttributes({});
  }, [
    connection,
    credentials,
    invitation,
    userData,
    verifiedCredAttributes,
    location,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <Frame id="app-frame">
        <Header>
          <div>
            <HeaderWrapperLeft onClick={() => navigate("/")}>
              <Logo
                src={indicioBankingLogo}
                style={{ width: "157px", height: "40px" }}
                alt="Indicio Banking logo"
              />
            </HeaderWrapperLeft>

            <HeaderWrapperRight>
              <div>
                <HeaderOption>
                  <DropdownArrowIcon />
                  <p>Services</p>
                </HeaderOption>
                <p>About Us</p>
                <HeaderOption>
                  <SearchIcon />
                  <p>Search</p>
                </HeaderOption>
              </div>
              <div>
                <ButtonLight onClick={() => navigate("/create-account")}>
                  Create Account
                </ButtonLight>
                <ButtonDark onClick={() => navigate("/login")}>
                  Log In
                </ButtonDark>
              </div>
            </HeaderWrapperRight>
          </div>
        </Header>

        <MainWrapper>
          <Routes>
            <Route
              path="/account"
              element={<AccountPage userData={userData} />}
            />
            <Route
              path="/create-account"
              element={
                <AccountCreationPage
                  connection={connection}
                  credentials={credentials}
                  createInvitation={createInvitation}
                  fetchVerification={fetchVerification}
                  issueCredentials={issueCredentials}
                  invitation={invitation}
                  resetAppState={resetAppState}
                  setUserData={setUserData}
                  userData={userData}
                  verifiedCredAttributes={verifiedCredAttributes}
                />
              }
            />
            <Route
              path="/login/credentials"
              element={
                <CredentialLoginPage
                  connection={connection}
                  createInvitation={createInvitation}
                  fetchVerification={fetchVerification}
                  invitation={invitation}
                  resetAppState={resetAppState}
                  setUserData={setUserData}
                  verifiedCredAttributes={verifiedCredAttributes}
                />
              }
            />
            <Route path="/error" element={<ErrorPage setError={setError} />} />
            <Route path="/login" element={<LoginPage />} />
            <Route exact path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </MainWrapper>
        <Footer>
          <FooterWrapper>
            <FooterWrapperFirst>
              <div>
                <img
                  src={indicioBankingLogo}
                  style={{ width: "157px", height: "40px" }}
                  alt="Indicio Banking logo"
                />
                <img src={logoIndicio} alt="Indicio logo" />
              </div>
              <div>
                <button>ATM Locator</button>
                <button>
                  <a href="https://indicio.tech/contact/">Contact Us</a>
                </button>
              </div>
            </FooterWrapperFirst>
            <FooterWrapperSecond>
              <a href="https://indicio.tech/contact/">About Us</a>
              <a href="/">Online Banking</a>
              <a href="/">Personal Loans</a>
              <a href="/">Home Loans</a>
              <a href="/">Car Loans</a>
              <a href="/">Terms of Service</a>
              <a href="/">Privacy Policy</a>
            </FooterWrapperSecond>
            <FooterWrapperThird>
              <p>
                This website is for demonstrational purposes only. Indicio does
                not provide banking services. Copyright © 2024 Indicio. All
                rights reserved.
              </p>
            </FooterWrapperThird>
          </FooterWrapper>
        </Footer>
      </Frame>
    </ThemeProvider>
  );
}

export default App;
